import type { ProductVariant, RawProductAttribute } from "@graphql/generated/types";
import { CollectionSegment } from "@lib/constants/product";
import { Attributes } from "@lib/enums/ProductAttributes";
import { getProductAttributeValue } from "@lib/utils/attributesUtils";
import {
    hasOneSize,
    isSizeProbablyASockSize,
    makeNonShoeSizePrettySize,
} from "@lib/utils/productUtility";

export interface ShoeSizeSelectable {
    sizeEU: number;
    sizeUS: number;
    sizeUK?: number;
    sizeCN?: number;
    sizeCA?: number;
    sizeKR?: number;
    sizeJP?: number;
    sizeData: number;
    available: boolean;
}

export interface NotShoeSizeSelectable {
    sizeData: number;
    sizeLabel: string;
    available: boolean;
}

const getSizes = (productVariant: ProductVariant) => {
    const { attributesRaw, availability } = productVariant;

    const sizeFromAttributes = getProductAttributeValue(attributesRaw, Attributes.size);

    const sizes = [
        Attributes.sizeUS,
        Attributes.sizeUK,
        Attributes.sizeCN,
        Attributes.sizeKR,
        Attributes.sizeJP,
        Attributes.sizeCA,
    ];

    const [sizeUS, sizeUK, sizeCN, sizeKR, sizeJP, sizeCA] = sizes.map((size) =>
        getProductAttributeValue(attributesRaw, size)
    );

    return {
        available: availability,
        sizeEU: sizeFromAttributes,
        sizeUS,
        sizeCN,
        sizeUK,
        sizeKR,
        sizeJP,
        sizeCA,
        sizeData: sizeFromAttributes,
    };
};

export const getShoeSizeSelectables = (productVariants: ProductVariant[]): ShoeSizeSelectable[] => {
    const result: ShoeSizeSelectable[] = [];

    productVariants.forEach((productVariant) => {
        const { available, sizeEU, sizeUS, sizeCN, sizeUK, sizeKR, sizeJP, sizeCA, sizeData } =
            getSizes(productVariant);

        const findStoreKeyAvailability = available?.channels?.results?.find(
            (item) => item?.channel?.key === process.env.NEXT_PUBLIC_STORE_KEY
        );
        if (typeof findStoreKeyAvailability?.availability?.isOnStock === "undefined") {
            return;
        }

        result.push({
            available: findStoreKeyAvailability?.availability?.isOnStock,
            sizeEU,
            sizeUS,
            sizeCN,
            sizeUK,
            sizeKR,
            sizeJP,
            sizeCA,
            sizeData,
        });
    });

    return result.sort((a, b) => a.sizeData - b.sizeData);
};

export const getNotShoeSizeSelectables = (
    productVariants: ProductVariant[]
): NotShoeSizeSelectable[] => {
    const result: NotShoeSizeSelectable[] = [];

    const isProbablySocks = isProductProbablyASock(productVariants);

    productVariants.forEach((productVariant) => {
        const { available, sizeEU, sizeUS, sizeCN, sizeUK, sizeKR, sizeJP, sizeCA, sizeData } =
            getSizes(productVariant);

        const sizesData = {
            sizeEU: makeNonShoeSizePrettySize(sizeEU),
            sizeUS,
            sizeCN,
            sizeUK,
            sizeKR,
            sizeJP,
            sizeCA,
        };

        const findStoreKeyAvailability = available?.channels?.results?.find(
            (item) => item?.channel?.key === process.env.NEXT_PUBLIC_STORE_KEY
        );
        if (typeof findStoreKeyAvailability?.availability?.isOnStock === "undefined") {
            return;
        }

        result.push({
            available: findStoreKeyAvailability?.availability?.isOnStock,
            sizeData,
            sizeLabel: isProbablySocks ? makeNonShoeSizePrettySize(sizeEU) : sizeEU,
            ...sizesData,
        });
    });

    return result.sort((a, b) => a.sizeData - b.sizeData);
};

export const getVariantSize = (productVariants: ProductVariant[]): string => {
    return getProductAttributeValue(productVariants[0].attributesRaw, Attributes.size);
};

const isCollectionSegment = (
    attributes: RawProductAttribute[],
    locale: string,
    segment: CollectionSegment
) => getProductAttributeValue(attributes, Attributes.CollectionSegment, locale) === segment;

export const isFootwear = (attributes: RawProductAttribute[], locale: string) =>
    isCollectionSegment(attributes, locale, CollectionSegment.Footwear);

export const isSizeProbablyOneSize = (productVariants: ProductVariant[]): boolean => {
    const sizeAttributeOfFirstVariant = getVariantSize(productVariants);

    return hasOneSize(sizeAttributeOfFirstVariant);
};

export const isProductProbablyASock = (productVariants: ProductVariant[]): boolean => {
    const sizeAttributeOfFirstVariant = getVariantSize(productVariants);

    // Sock size is expressed as Number with format XXXX
    return isSizeProbablyASockSize(sizeAttributeOfFirstVariant);
};
